/**
 * NotificationsPage - full list of notifications
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, MessageCircle, UserPlus, Music, Loader2, CheckCheck, ArrowLeft, Trash2, Camera } from 'lucide-react';
import { notificationsApi } from '../api/notifications';
import { useAuth } from '../context/AuthContext';

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const iconFor = (type) => {
  switch (type) {
    case 'like': return <Heart className="w-5 h-5 text-[#FF007F]" />;
    case 'new_message':
    case 'message': return <MessageCircle className="w-5 h-5 text-[#00F0FF]" />;
    case 'follow': return <UserPlus className="w-5 h-5 text-purple-400" />;
    case 'event_photos': return <Camera className="w-5 h-5 text-[#39FF14]" />;
    default: return <Music className="w-5 h-5 text-white/70" />;
  }
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifs = useCallback(async () => {
    try {
      const data = await notificationsApi.getMy(100);
      setItems(data.notifications || []);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchNotifs();
  }, [isAuthenticated, fetchNotifs, navigate]);

  const handleClick = async (n) => {
    if (!n.read) {
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      notificationsApi.markRead([n.id]).catch(() => {});
    }
    const url = n.metadata?.link_url;
    if (url) navigate(url);
  };

  const handleMarkAll = async () => {
    setItems(prev => prev.map(x => ({ ...x, read: true })));
    try { await notificationsApi.markAllRead(); } catch (err) { /* ignore */ }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setItems(prev => prev.filter(x => x.id !== id));
    try { await notificationsApi.remove(id); } catch (err) { /* ignore */ }
  };

  const hasUnread = items.some(n => !n.read);

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      <div className="sticky top-0 z-40 bg-[#050505]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
              data-testid="notifications-back-btn"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Notifications</h1>
          </div>
          {hasUnread && (
            <button
              onClick={handleMarkAll}
              className="flex items-center gap-1.5 text-[#00F0FF] text-sm font-medium hover:opacity-80"
              data-testid="notifications-mark-all-btn"
            >
              <CheckCheck className="w-4 h-4" /> Mark all read
            </button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF007F]" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Bell className="w-14 h-14 mx-auto mb-4 text-[#3F3F46]" />
            <h2 className="text-lg font-bold text-white mb-1">No notifications yet</h2>
            <p className="text-[#71717A] text-sm">Likes, follows and messages will show up here.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left transition-colors border ${!n.read ? 'bg-[#FF007F]/5 border-[#FF007F]/20' : 'bg-[#12101A] border-white/5 hover:bg-white/5'}`}
                data-testid={`notification-row-${n.id}`}
              >
                <div className="w-11 h-11 rounded-full overflow-hidden bg-white/10 flex items-center justify-center flex-shrink-0">
                  {n.fromUserPhoto ? (
                    <img src={n.fromUserPhoto} alt="" className="w-full h-full object-cover" />
                  ) : (
                    iconFor(n.type)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm leading-snug">{n.message}</p>
                  <span className="text-[#71717A] text-xs">{timeAgo(n.createdAt)}</span>
                </div>
                <div className="flex-shrink-0">{iconFor(n.type)}</div>
                <span
                  onClick={(e) => handleDelete(e, n.id)}
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
                  data-testid={`notification-delete-${n.id}`}
                >
                  <Trash2 className="w-4 h-4 text-[#71717A]" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
